/** Asset drawer — reuses the M5 shape; drives publish-now + cancel actions. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import type { CalendarEvent } from "@/entities/mission-control";
import { cancelSchedule, publishNow, PUBLISHING_TONES } from "@/entities/mission-control";
import { invalidateCampaignLifecycle } from "@/entities/campaign-run";
import { toast } from "sonner";

interface DrawerProps {
  workspaceId: string;
  event: CalendarEvent | null;
  onClose: () => void;
  formatFrame: (iso: string) => string;
}

export function AssetDrawer({ workspaceId, event, onClose, formatFrame }: DrawerProps): JSX.Element | null {
  const qc = useQueryClient();
  const publish = useMutation({
    mutationFn: () => publishNow(workspaceId, event!.id),
    onSuccess: (row) => {
      toast.success(`Publishing → ${row.publishing_status_label}`);
      qc.invalidateQueries({ queryKey: ["mc"] });
      // Patch 34.2.1c · lifecycle invalidation — refresh canonical
      // CampaignRun + every consumer (Home, Dashboard, Vault, Review).
      invalidateCampaignLifecycle(qc, workspaceId);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const cancel = useMutation({
    mutationFn: () => cancelSchedule(workspaceId, event!.id),
    onSuccess: () => {
      toast.success("Schedule cancelled");
      qc.invalidateQueries({ queryKey: ["mc"] });
      // Patch 34.2.1c · Unschedule is a lifecycle mutation — refresh
      // the canonical projection so every founder-facing counter
      // catches up.
      invalidateCampaignLifecycle(qc, workspaceId);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!event) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border/60 bg-card shadow-2xl" data-testid="mc-asset-drawer">
      <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {event.permanent_asset_id ?? event.asset_id}
          </div>
          <div className="text-sm font-semibold">{event.asset_title}</div>
        </div>
        <button
          onClick={onClose}
          className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          data-testid="mc-drawer-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
        <Row label="Channel">
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: event.channel_color }}
            />
            <span>{event.channel_label}</span>
          </span>
        </Row>
        <Row label="Content type">{event.content_type}</Row>
        <Row label="Campaign">{event.campaign_name ?? "—"}</Row>
        <Row label="Scheduled">{formatFrame(event.scheduled_at)}</Row>
        <Row label="Time zone">{event.workspace_time_zone}</Row>
        <Row label="Status">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${PUBLISHING_TONES[event.publishing_status]}`}>
            {event.publishing_status_label}
          </span>
        </Row>
        <Row label="Brand version">{event.brand_brain_version ?? "—"}</Row>
        <Row label="Creative brief">{event.creative_brief_id ?? "—"}</Row>
        <Row label="Series">{event.recurrence_series_id ?? "—"}</Row>
      </div>

      <div className="flex items-center gap-2 border-t border-border/60 px-5 py-3">
        <Button
          data-testid={`mc-publish-now-${event.id}`}
          onClick={() => publish.mutate()}
          disabled={publish.isPending || event.publishing_status === "published"}
          className="flex-1"
        >
          Publish now
        </Button>
        <Button
          data-testid={`mc-cancel-${event.id}`}
          variant="outline"
          onClick={() => cancel.mutate()}
          disabled={cancel.isPending || event.publishing_status === "cancelled"}
          className="flex-1"
        >
          Cancel schedule
        </Button>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/40 pb-2 last:border-b-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className="text-right text-xs text-foreground">{children}</span>
    </div>
  );
}
